import React, { useCallback, useRef, useState } from 'react';
import { FlexRow, TableDataState, TableEditButtons, usePaginatedQueryV8 } from 'motion-components';
import { Button, Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlGetQuery, gqlInitEdocSeries } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { handleExport } from './export-columns';
import { useApolloClient, useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { observer } from 'mobx-react-lite';
import { isFotokiklosi } from 'shared-pages/common/constants';

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const SeriesGridPage = observer(
  ({ collectorId, isGridInForm = false }: { collectorId?: number; isGridInForm?: boolean }) => {
    const { appStore, tableStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('MaterialsGridPage');

    const location = useLocation<any>();
    const history = useHistory();
    const [selected, setSelected] = useState<any>(null);
    const { t } = useTranslation();
    const persistedValues = isGridInForm ? { collectorId } : {};
    const { canView, canAddNew, canEdit, canDelete } = userPermissions();
    function userPermissions() {
      if (isFotokiklosi) {
        if (appStore.isPlatformOwner)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      } else {
        if (appStore.isPlatformOwner)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollector)
          return { canView: true, canAddNew: true, canEdit: true, canDelete: true };
        else if (appStore.isCollectorDriver)
          return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
        else return { canView: true, canAddNew: false, canEdit: false, canDelete: false };
      }
    }

    const handleAddNew = () => {};
    const [initEdocSeries] = useMutation(gqlInitEdocSeries);
    const initSeries = async () => {
      const res = await initEdocSeries({
        variables: { collectorId: collectorId ? collectorId : 0 },
      }).catch((reason) => {
        console.error('initEdocSeries', reason);
        handleGraphqlError('mutation-initEdocSeries-failed', reason);
      });
      if (res) {
        refetch();
      }
    };
    /* ------------------- Query ------------------------------- */
    const initOrderBy = 'docPrefix ASC';
    const query = usePaginatedQueryV8(
      gqlGetQuery,
      buildWhereFromFilterValues,
      location.state?.order ?? initOrderBy,
      appStore,
      true,
      { isActive: true, collectorId: collectorId },
      !isGridInForm, // History Replace
      !isGridInForm ? undefined : 'series_grid',
      persistedValues,
    );
    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      filtersChanged,
      gridPageChanged,
      queryState,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const client = useApolloClient();

    const canCreateNew = true;
    const handleCreateNew = useCallback(() => {
      history.push({
        pathname: generatePath(getRouteByName('SeriesForm'), { id: -1 }),
        state: {
          pathname: location.pathname,
          ...location.state,
        },
      });
    }, [history, location.pathname, location.state]);

    const gridRef = useRef();

    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    } else {
      return (
        <Card className="shadow">
          {!isGridInForm && (
            <Card.Header>
              <FlexRow>
                <Card.Title className="m-0">{t('Σειρές Αρίθμησης')}</Card.Title>
                {/* {canAddNew && <ButtonNewRecord onClick={handleAddNew} size="sm" />} */}
                {/* Table Edit Buttons  */}
                <TableEditButtons
                  className="me-3"
                  canEdit={canEdit}
                  canInsert={canAddNew}
                  canDelete={canDelete}
                  tableOptionsMeta={() => {
                    if (gridRef.current) return (gridRef.current as any).table.options.meta;
                    else return undefined;
                  }}
                  twoLines={false}
                />
              </FlexRow>
            </Card.Header>
          )}
          <Card.Body className="nopadding">
            {/* Φίλτρα */}
            <Filters
              onChange={filtersChanged}
              initialValues={queryState?.filterValues ?? {}}
              disable={tableStore.state !== TableDataState.browse}
              isGridInForm={isGridInForm}
            />

            {/* Grid */}
            <DataGrid
              ref={gridRef}
              loading={loading}
              data={data}
              dataCount={dataCount}
              initPageIndex={initPageIndex}
              onPageChange={gridPageChanged}
              handleExport={() => {
                handleExport(client, queryState, t);
              }}
              initOrderBy={queryParams?.order ?? initOrderBy}
              onOrderChange={(orderBy) => {
                const newQueryParams = { ...queryParams, order: orderBy };
                setQueryParams(newQueryParams);
                if (!isGridInForm) {
                  history.replace({
                    pathname: location.pathname,
                    state: {
                      ...location?.state,
                      order: orderBy,
                    },
                  });
                }
              }}
              selected={selected}
              setSelected={setSelected}
              refetch={refetch}
              isGridInForm={isGridInForm}
            />
          </Card.Body>
          <Card.Footer>
            {appStore.isPlatformOwner && (
              <Button size="sm" onClick={initSeries}>
                {t('Αρχικοποίηση Σειρών Αρίθμησης')}
              </Button>
            )}
          </Card.Footer>
        </Card>
      );
    }
  },
);
